import { createElement, useCallback, useRef, useState } from 'react';
import { ConfirmDialog } from '@/features/common/ConfirmDialog/ConfirmDialog';
import type { ConfirmRequest } from '@/features/common/ConfirmDialog/types/ConfirmRequest';
import type { ReasonConfirmRequest } from '@/features/common/ConfirmDialog/types/ReasonConfirmRequest';

/**
 * Asks a ConfirmDialog question from inside a handler, as window.confirm did:
 * `if (!(await confirm({...}))) return;`. The caller renders `dialog` once, anywhere in its tree.
 *
 * <p>`confirmWithReason` answers the trimmed reason, or null when the question was cancelled — an
 * optional reason left empty confirms with ''. A second question asked while one is open cancels
 * the first. No context and no provider: each screen owns the one dialog it asks through.
 *
 * <p>The request stays after the answer so the dialog does not go blank while it closes.
 */
export function useConfirm() {
  const [request, setRequest] = useState<ConfirmRequest | ReasonConfirmRequest | null>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  // Held in a ref: the dialog's close event fires after the answer, and must find nothing to settle.
  const settle = useRef<((answer: string | null) => void) | null>(null);

  const ask = useCallback(
    (next: ConfirmRequest | ReasonConfirmRequest, onAnswer: (answer: string | null) => void) => {
      settle.current?.(null);
      settle.current = onAnswer;
      setRequest(next);
      setReason('');
      setOpen(true);
    },
    [],
  );

  const answer = (value: string | null) => {
    const pending = settle.current;
    settle.current = null;
    setOpen(false);
    pending?.(value);
  };

  const confirm = useCallback(
    (next: ConfirmRequest) =>
      new Promise<boolean>((resolve) => ask(next, (value) => resolve(value !== null))),
    [ask],
  );

  const confirmWithReason = useCallback(
    (next: ReasonConfirmRequest) => new Promise<string | null>((resolve) => ask(next, resolve)),
    [ask],
  );

  const asksReason = request !== null && 'reasonLabel' in request;
  const dialog = createElement(ConfirmDialog, {
    open,
    title: request?.title ?? '',
    body: request?.body ?? null,
    confirmLabel: request?.confirmLabel ?? '',
    cancelLabel: request?.cancelLabel,
    tone: request?.tone ?? 'danger',
    reason: asksReason
      ? {
          label: request.reasonLabel,
          value: reason,
          onChange: setReason,
          required: request.reasonRequired,
        }
      : undefined,
    onConfirm: () => answer(asksReason ? reason.trim() : ''),
    onCancel: () => answer(null),
  });

  return { confirm, confirmWithReason, dialog };
}
